// src/utils/personalLogin.js
// SSO personal-user login: resolves a mobile-signed token (attached to a
// product link by the paymeo-webapp marketplace) into a personal profile so
// the cart/checkout form can be auto-filled.
import api from "./api";
import { API_CONFIG } from "./apiConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "paymeo_personal_user";
const TOKEN_KEY = "paymeo_personal_token";
const ADDRESSES_KEY = "paymeo_personal_addresses";

export const getStoredPersonalUser = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const setStoredPersonalUser = (user) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } catch (error) {
    // Ignore storage failures (private mode etc.)
  }
};

export const clearStoredPersonalUser = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADDRESSES_KEY);
  } catch (error) {
    // Ignore
  }
};

export const getStoredPersonalToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch (error) {
    return "";
  }
};

export const setStoredPersonalToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    // Ignore
  }
};

export const getStoredAddresses = () => {
  try {
    const raw = localStorage.getItem(ADDRESSES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
};

export const setStoredAddresses = (addresses) => {
  try {
    localStorage.setItem(ADDRESSES_KEY, JSON.stringify(addresses || []));
  } catch (error) {
    // Ignore
  }
};

/**
 * Exchange the personal token for a profile via the business backend.
 * Never throws — returns the profile object or null on failure.
 */
export const resolvePersonalLogin = async (token) => {
  if (!token) return null;
  try {
    const { data } = await api.post(API_CONFIG.PERSONAL.LOGIN, { token });
    if (data && data.success && data.user) {
      setStoredPersonalToken(token);
      setStoredPersonalUser(data.user);
      if (Array.isArray(data.addresses)) setStoredAddresses(data.addresses);
      return data.user;
    }
    return null;
  } catch (error) {
    console.error("Personal login failed:", error?.response?.data?.error || error.message);
    return null;
  }
};

/**
 * Fetch the personal user's saved addresses on demand from the
 * invoice_generator proxy (which forwards to the mobile address model).
 * Never throws — returns the array of addresses, or null when not logged in.
 */
export const fetchPersonalAddresses = async () => {
  const token = getStoredPersonalToken();
  if (!token) return null;
  try {
    const { data } = await api.get(API_CONFIG.PERSONAL.ADDRESSES, {
      headers: { "x-personal-token": token }
    });
    if (!data || !data.success) return null;
    const list = Array.isArray(data.addresses) ? data.addresses : [];
    setStoredAddresses(list);
    return list;
  } catch (error) {
    console.error("Fetch addresses failed:", error?.response?.data?.error || error.message);
    return null;
  }
};

/**
 * Save a new address. Logged-in personal users are saved through the
 * invoice_generator proxy (which forwards to the mobile backend's address
 * model). Guests (no token) keep the address locally only, so it is available
 * for this order's delivery without needing a mobile account.
 */
export const savePersonalAddress = async ({ label, fullName, email, phone, streetAddress, city, state, country, isDefault }) => {
  const token = getStoredPersonalToken();
  if (!token) {
    const saved = {
      id: `local-${Date.now()}`,
      label: label || "Home",
      fullName,
      email: email || null,
      phone,
      streetAddress,
      city,
      state,
      country: country || "Nigeria",
      isDefault: !!isDefault,
      local: true,
      createdAt: new Date().toISOString()
    };
    const next = [
      ...(getStoredAddresses() || []).map(a => (saved.isDefault ? { ...a, isDefault: false } : a)),
      saved
    ];
    setStoredAddresses(next);
    return saved;
  }
  try {
    const { data } = await api.post(API_CONFIG.PERSONAL.CREATE_ADDRESS, {
      token,
      label,
      fullName,
      email,
      phone,
      streetAddress,
      city,
      state,
      country,
      isDefault: !!isDefault
    });
    if (!data || !data.success) return null;
    const saved = {
      id: data.data?.id,
      label: label || "Home",
      fullName,
      email: email || null,
      phone,
      streetAddress,
      city,
      state,
      country: country || "Nigeria",
      isDefault: !!isDefault,
      createdAt: new Date().toISOString()
    };
    const next = [
      ...(getStoredAddresses() || []).map(a => (saved.isDefault ? { ...a, isDefault: false } : a)),
      saved
    ];
    setStoredAddresses(next);
    return saved;
  } catch (error) {
    console.error("Save address failed:", error?.response?.data?.error || error.message);
    return null;
  }
};

/**
 * Persist a "default" preference change for an existing address through the
 * invoice_generator proxy (PUT to the mobile backend's address model).
 */
export const setDefaultPersonalAddress = async (id) => {
  const token = getStoredPersonalToken();
  if (!token || !id) return false;
  try {
    const { data } = await api.put(`${API_CONFIG.PERSONAL.UPDATE_ADDRESS}/${id}`, {
      token,
      isDefault: true
    });
    if (!data || !data.success) return false;
    const next = (getStoredAddresses() || []).map((a) => ({ ...a, isDefault: a.id === id }));
    setStoredAddresses(next);
    return true;
  } catch (error) {
    console.error("Set default address failed:", error?.response?.data?.error || error.message);
    return false;
  }
};

export default resolvePersonalLogin;
